import { useEffect, useState } from "react";
import { When } from "react-if";
import {
  CheckCircleIcon,
  PaperAirplaneIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import { toast } from "react-toastify";
import {
  CUSTOM_DOMAIN_INVALID_MESSAGE,
  CUSTOM_DOMAIN_REGEX,
  OrganisationEmailProvider,
} from "shared";

import { useAllowedFeature } from "../../../../hooks/useAllowedFeature";
import { useHasValidSubscription } from "../../../../hooks/useHasValidSubscription";
import { classNames } from "../../../../../common/lib/classNames";
import { InputTextAddon } from "../../../../../common/Atoms/InputTextAddon";
import { Paragraph } from "../../../../../common/Atoms/Typography/Paragraph";
import { useCustomDomain } from "../../../../hooks/useCustomDomain";
import { TwoColFormRow } from "../../../Molecules/Form/TwoColFormRow";
import { UpgradeBadge } from "../../../Molecules/UpgradeBadge";
import {
  useGetCustomDomainLinkableQuery,
  useRequestCustomDomainAccessMutation,
  useUpdateCustomDomainMutation,
  useVerifyCustomDomainMutation,
} from "../../../../services/api/customDomainApi/customDomain";
import { useDebounce } from "../../../../hooks/useDebounce";
import { Button } from "../../../../../common/Atoms/Button";
import { Notification } from "../../../../../common/Atoms/Notification";
import { useGetSelectedOrganisation } from "../../../../hooks/useGetSelectedOrganisation";

import { DNSInfo } from "./DNSInfo";
import { LinkCustomDomain } from "./LinkCustomDomain";
import { SendDnsModal } from "./SendDnsModal";
import { SpfCheckTip } from "./SpfCheckTip";

export function CustomDomain() {
  const allowedFeature = useAllowedFeature(`customDomain`);
  const hasValidSub = useHasValidSubscription();
  // Active subscription on a plan without the feature. No subscription yet = allowed to set up (domain not used until subscribed).
  const locked = hasValidSub && !allowedFeature;
  const [verify, { isLoading: verifyLoading }] = useVerifyCustomDomainMutation();
  const [requestAccess, { isLoading: requestLoading }] = useRequestCustomDomainAccessMutation();
  const [updateDomain, { isLoading: saveLoading }] = useUpdateCustomDomainMutation();

  const { data: customDomain } = useCustomDomain();
  const organisation = useGetSelectedOrganisation();
  const organisationId = organisation?.id || ``;
  const [sendDnsOpen, setSendDnsOpen] = useState(false);

  // The domain has its own save, separate from the general settings form
  const savedDomain = customDomain?.domain || ``;
  const [draft, setDraft] = useState(savedDomain);

  useEffect(() => {
    setDraft(savedDomain);
  }, [ savedDomain ]);

  const cleanDraft = draft.trim().toLowerCase();
  const dirty = cleanDraft !== savedDomain;
  const draftInvalid = !!cleanDraft && !CUSTOM_DOMAIN_REGEX.test(cleanDraft);

  const providerLocked = !!organisation && organisation.emailProvider !== OrganisationEmailProvider.SENDGRID;

  // Domains verified in other orgs the user administers, offered upfront so they can be linked without DNS work
  const { data: linkable } = useGetCustomDomainLinkableQuery(
    { organisationId },
    { skip: !organisationId || locked || providerLocked },
  );
  // While the user types a new domain, check whether it is already verified somewhere they can't link from
  const typedDomain = useDebounce(cleanDraft, 500);
  const typedDomainIsNew = !!typedDomain && typedDomain !== savedDomain && CUSTOM_DOMAIN_REGEX.test(typedDomain);
  const { data: typedCheck } = useGetCustomDomainLinkableQuery(
    { organisationId, domain: typedDomain },
    { skip: !organisationId || !typedDomainIsNew || locked || providerLocked },
  );
  const blockedDomain = typedDomainIsNew && typedCheck?.verifiedElsewhere && typedCheck.linkable.length === 0
    ? typedDomain
    : undefined;

  async function handleRequestAccess() {
    try {
      await requestAccess(organisationId).unwrap();
      toast.success(`Request sent! We'll be in touch shortly.`);
    }
    catch {
      toast.error(`Could not send the request. Please try again.`);
    }
  }

  async function handleSave() {
    if (draftInvalid) {
      toast.error(CUSTOM_DOMAIN_INVALID_MESSAGE);

      return;
    }

    try {
      await updateDomain({ organisationId, domain: cleanDraft || null }).unwrap();
      toast.success(cleanDraft ? `Domain saved. Add the DNS records below, then verify.` : `Custom domain removed`);
    }
    catch (err) {
      toast.error(err?.data || `Could not save the domain. Please try again.`);
    }
  }

  if (!customDomain) {
    return null;
  }

  const hasDomainValue = !!savedDomain;
  const dnsRecords = customDomain.dnsRecords;
  const allRecordsVerified = customDomain.verified && dnsRecords.every(r => r.verified);

  const badge = !allowedFeature && (
    <UpgradeBadge />
  );

  let addOnElement = null;

  if (hasDomainValue && customDomain.verified) {
    addOnElement = (
      <span className={ `flex` }>
        <CheckCircleIcon className={ `h-5 w-5 text-green-500` } />
        <Paragraph
          variant={ `primary` }
          className={ `ml-1` }
        >
          { `Verified & Active` }
        </Paragraph>
      </span>
    );
  }

  if (hasDomainValue && !customDomain.verified) {
    addOnElement = (
      <span className={ `flex` }>
        <XMarkIcon className={ `h-5 w-5 text-red-500` } />
        <Paragraph
          variant={ `error` }
          className={ `ml-1` }
        >
          { `Not verified` }
        </Paragraph>
      </span>
    );
  }

  const help = locked ? `Upgrade to "Pro" to unlock custom domains` : undefined;

  if (providerLocked) {
    return (
      <TwoColFormRow
        heading={ `Custom Domain` }
        badge={ badge }
        help={ `Custom domains aren't yet available on your account's email setup.` }
      >
        <div className={ `space-y-2` }>
          <Paragraph variant={ `secondary` }>
            { `Your account uses an email setup that doesn't support custom domains yet. Request access and our team will be in touch.` }
          </Paragraph>
          <Button
            onClick={ handleRequestAccess }
            loading={ requestLoading }
            disabled={ requestLoading }
          >
            { `Request Access` }
          </Button>
        </div>
      </TwoColFormRow>
    );
  }

  return (
    <TwoColFormRow
      heading={ `Custom Domain` }
      badge={ badge }
      help={ help }
    >
      <div className={ classNames(
        `space-y-2`,
        locked ? `opacity-50` : ``,
      ) }>
        <InputTextAddon
          label={ `URL` }
          value={ draft }
          name={ `customDomain` }
          onChange={ e => setDraft(String(e.value)) }
          disabled={ locked }
          subHeading={ `Enter your domain using only the domain name and TLD (e.g. example.com).` }
          invalid={ draftInvalid ? { path: `customDomain`, message: CUSTOM_DOMAIN_INVALID_MESSAGE } : undefined }
          placeholder={ `example.com` }
          addOnText={ addOnElement }
        />
        <When condition={ dirty }>
          <div className={ `flex justify-end gap-3` }>
            <Button
              onClick={ () => setDraft(savedDomain) }
              color={ `gray` }
              disabled={ saveLoading }
            >
              { `Cancel` }
            </Button>
            <Button
              onClick={ handleSave }
              loading={ saveLoading }
              disabled={ saveLoading || locked || draftInvalid }
            >
              { cleanDraft ? `Save Domain` : `Remove Domain` }
            </Button>
          </div>
        </When>
        <When condition={ (linkable?.linkable.length || 0) > 0 || !!blockedDomain }>
          {
            () => (
              <LinkCustomDomain
                key={ linkable?.linkable.map(l => l.organisationId).join(`,`) }
                organisationId={ organisationId }
                linkable={ linkable?.linkable || [] }
                blockedDomain={ blockedDomain }
              />
            )
          }
        </When>
        <When condition={ hasDomainValue }>
          {
            () => (
              <SpfCheckTip
                organisationId={ organisationId }
                domain={ savedDomain }
              />
            )
          }
        </When>
        <When condition={ hasDomainValue && dnsRecords.length > 0 }>
          {
            () => (
              <>
                <DNSInfo rows={ dnsRecords } />
                <div className={ `flex justify-end gap-3 pt-2` }>
                  <Button
                    onClick={ () => setSendDnsOpen(true) }
                    color={ `gray` }
                    icon={ <PaperAirplaneIcon className={ `h-4 w-4 ml-2` } /> }
                    disabled={ dirty }
                    tooltip={ dirty ? `Save your domain first` : `Email these DNS records to someone else, e.g. your IT person` }
                  >
                    { `Send to someone` }
                  </Button>
                  <SendDnsModal
                    open={ sendDnsOpen }
                    setOpen={ setSendDnsOpen }
                    organisationId={ organisationId }
                    domain={ savedDomain }
                  />
                  <Button
                    onClick={ () => verify(organisationId) }
                    loading={ verifyLoading }
                    disabled={ verifyLoading || allRecordsVerified || dirty }
                    color={ `red` }
                    tooltip={
                      allRecordsVerified ?
                        `All records verified`
                        :
                        `Some records are not yet verified`
                    }
                  >
                    { `Verify Domain` }
                  </Button>
                </div>
              </>
            )
          }
        </When>
        <When condition={ !hasValidSub }>
          <Notification
            type={ `info` }
            className={ `mt-4` }
            title={ `Set up now, used once you subscribe` }
          >
            { `You can add and verify your domain now. Emails will be sent from it once you subscribe to a plan that includes custom domains.` }
          </Notification>
        </When>
      </div>
    </TwoColFormRow>
  );
}
